// Where Meta's own tools live, and what they are called — REQ-264 AC2 with REQ-002 AC3.
//
// The rule these constants exist to keep: **a link out of AdOrchestrator never carries an identifier.** No
// `act_<id>`, no campaign id, no query string at all — each URL is the tool's own home, and the sentence
// beside it tells the user where to look once they are there. That is why these are plain constants rather
// than something built from `account.id`: there is nothing to interpolate, so nothing can leak.
//
// Whether the pointer is a live link at all is the account's decision, never ours: `capabilities
// .canOpenAdsManager` is false on a demo account (there is nothing of yours to open), and the control is
// then rendered disabled with the `ads_manager.sample_data` reason beside it — never a link that goes
// nowhere (w04's rule, now used by every "Create in Ads Manager" pointer too).
//
// **Ads Manager's own address is core's, not this file's** (T-370; D-854 / D-856's debt). The server writes
// the very same string into a live account's `AdsManagerLink` (API §4.4, `kind: "home"`), so the URL this
// app draws a pointer from and the URL the contract answers are ONE constant — `ADS_MANAGER_HOME_URL` on
// `@sl/core`'s root entry — rather than three copies held together by nobody.

import { ADS_MANAGER_HOME_URL } from '@sl/core';

export interface MetaTool {
  /** What Meta calls the place, so the sentence can name it. */
  readonly name: string;
  readonly url: string;
}

export const META_TOOL = {
  adsManager: { name: 'Ads Manager', url: ADS_MANAGER_HOME_URL },
  billing: { name: 'Billing & payments', url: 'https://business.facebook.com/billing_hub/' },
  accountQuality: { name: 'Account quality', url: 'https://business.facebook.com/accountquality/' },
} as const satisfies Record<string, MetaTool>;

/** Every URL this app may send a user to on Meta — what the "no identifiers" test walks. */
export const META_TOOL_URLS: readonly string[] = Object.values(META_TOOL).map((t) => t.url);

/** The label and the copy of a "Create in Ads Manager" pointer (REQ-264 AC3, REQ-043 AC3). */
export const ADS_MANAGER_COPY = {
  create: 'Create in Ads Manager',
  open: 'Open Ads Manager',
  /** Said once beside the control, so nobody expects the product to grow a create flow. */
  createNote:
    'Campaigns are made in Meta. AdOrchestrator watches what they do and helps you steer them.',
} as const;
